import React from 'react';
import {brand} from '../../brand';

// Mirrors flutter/lib/widgets/timer/circular_timer_ring.dart:
//   - 96 radial tick marks around the perimeter
//   - Inactive ticks render at low alpha (backdrop)
//   - Active ticks fade from a dim trailing tone into `color`
//   - Most-recent active ticks blend toward `leadingColor` (warm burst)
//   - Leading-edge ticks get a drop-shadow glow

const DEFAULT_TICK_COUNT = 96;
const TICK_LENGTH_FACTOR = 0.10;
const DEFAULT_STROKE_WIDTH = 5;

type Rgb = [number, number, number];

const hexToRgb = (hex: string): Rgb => {
  const c = hex.replace('#', '');
  return [
    parseInt(c.slice(0, 2), 16),
    parseInt(c.slice(2, 4), 16),
    parseInt(c.slice(4, 6), 16),
  ];
};

const rgbaString = (rgb: Rgb, alpha: number): string =>
  `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${alpha})`;

const blendRgb = (a: Rgb, b: Rgb, t: number): Rgb => [
  Math.round(a[0] + (b[0] - a[0]) * t),
  Math.round(a[1] + (b[1] - a[1]) * t),
  Math.round(a[2] + (b[2] - a[2]) * t),
];

export type TickRingProps = {
  size: number;
  progress: number;
  color: string;
  leadingColor?: string;
  inactiveColor?: string;
  tickCount?: number;
  strokeWidth?: number;
};

export const TickRing: React.FC<TickRingProps> = ({
  size,
  progress,
  color,
  leadingColor = brand.timerComplete,
  inactiveColor = brand.borderLight,
  tickCount = DEFAULT_TICK_COUNT,
  strokeWidth = DEFAULT_STROKE_WIDTH,
}) => {
  const cx = size / 2;
  const cy = size / 2;
  const tickLength = size * TICK_LENGTH_FACTOR;
  const outerR = size / 2 - strokeWidth / 2;
  const innerR = outerR - tickLength;

  const clampedProgress = Math.max(0, Math.min(1, progress));
  const activeTicks = Math.max(
    0,
    Math.min(tickCount, Math.round(tickCount * clampedProgress)),
  );
  const trailingFadeSpan = Math.max(1, Math.round(tickCount * 0.18));
  const leadingFadeSpan = Math.max(1, Math.round(tickCount * 0.12));

  const progressRgb = hexToRgb(color);
  const leadingRgb = hexToRgb(leadingColor);
  const inactiveRgb = hexToRgb(inactiveColor);

  const ticks: React.ReactNode[] = [];
  for (let i = 0; i < tickCount; i++) {
    const angle = -Math.PI / 2 + 2 * Math.PI * (i / tickCount);
    const cosA = Math.cos(angle);
    const sinA = Math.sin(angle);
    const x1 = cx + cosA * innerR;
    const y1 = cy + sinA * innerR;
    const x2 = cx + cosA * outerR;
    const y2 = cy + sinA * outerR;

    let stroke: string;
    let glow = false;
    if (i >= activeTicks) {
      stroke = rgbaString(inactiveRgb, 0.6);
    } else {
      const trailingT = Math.min(1, i / trailingFadeSpan);
      const eased = 1 - Math.pow(1 - trailingT, 3);
      const baseAlpha = 0.28 + (1 - 0.28) * eased;

      const fromLeading = activeTicks - 1 - i;
      let leadingT = 0;
      if (fromLeading < leadingFadeSpan) {
        leadingT = Math.max(0, Math.min(1, 1 - fromLeading / leadingFadeSpan));
      }
      const tickRgb =
        leadingT > 0 ? blendRgb(progressRgb, leadingRgb, leadingT * leadingT) : progressRgb;
      stroke = rgbaString(tickRgb, baseAlpha);
      if (leadingT > 0.4) glow = true;
    }

    ticks.push(
      <line
        key={i}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        style={glow ? {filter: `drop-shadow(0 0 6px ${leadingColor}aa)`} : undefined}
      />,
    );
  }

  return (
    <svg width={size} height={size}>
      {ticks}
    </svg>
  );
};
